// Add or edit a business on the Trade tab. Adding can start from one of the
// Outreach tab's leads (copies its details across and links the two), and can
// log the first visit/call and the next step in the same go.
import { useState } from 'react';
import { api } from '../lib/auth';
import { Button, Input, Select, Textarea, ModalShell } from '../lib/ui';
import { STAGES, ACTIVITY_TYPES, addDays, todayYmd } from '../lib/trade';
import { Label, errorText } from './TradeCommon';

const fromAccount = (a) => ({
  businessName: a?.businessName || '',
  contactName:  a?.contactName || '',
  phone:        a?.phone || '',
  email:        a?.email || '',
  line1:        a?.address?.line1 || '',
  line2:        a?.address?.line2 || '',
  city:         a?.address?.city || '',
  postcode:     a?.address?.postcode || '',
  stage:        a?.stage || 'prospect',
  potentialWeekly: a?.potentialWeekly ?? '',
  interestedIn: a?.interestedIn || '',
  termsDays:    a?.termsDays ?? '',
  nextStep:     '',
  nextStepDate: '',
});

function LeadPicker({ onPick }) {
  const [leads, setLeads] = useState(null);
  const [q, setQ] = useState('');
  const [error, setError] = useState('');

  const load = async () => {
    if (leads) return;
    try {
      setLeads((await api.get('/leads')).data);
    } catch (e) {
      setError(errorText(e));
    }
  };

  const term = q.trim().toLowerCase();
  const matches = term && leads
    ? leads.filter(l => [l.businessName, l.contactName, l.email, l.location].some(v => (v || '').toLowerCase().includes(term))).slice(0, 8)
    : [];

  return (
    <div>
      <Label hint="optional">Start from an Outreach lead</Label>
      <Input placeholder="Search the businesses you've emailed or messaged…" value={q} onFocus={load} onChange={e => setQ(e.target.value)} />
      {error && <div className="trade-form__hint" style={{ color: 'var(--bad-fg)' }}>{error}</div>}
      {matches.length > 0 && (
        <ul className="trade-lead-results">
          {matches.map(l => (
            <li key={l._id} onClick={() => { onPick(l); setQ(''); }}>
              {l.businessName}<small>{[l.location, l.status].filter(Boolean).join(' · ')}</small>
            </li>
          ))}
        </ul>
      )}
      {term && leads && !matches.length && <div className="trade-form__hint">No leads match.</div>}
    </div>
  );
}

export default function TradeAccountModal({ account, onClose, onSaved }) {
  const adding = !account;
  const today = todayYmd();
  const [f, setF] = useState(() => fromAccount(account));
  const [lead, setLead] = useState(null);
  const [first, setFirst] = useState({ type: 'visit', text: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setF(prev => ({ ...prev, [k]: e.target.value }));

  const pickLead = (l) => {
    setLead(l);
    setF(prev => ({
      ...prev,
      businessName: l.businessName || prev.businessName,
      contactName:  l.contactName || prev.contactName,
      phone:        l.phone || prev.phone,
      email:        l.email || prev.email,
      city:         prev.city || l.location || '',
    }));
  };

  const save = async () => {
    if (!f.businessName.trim()) return setError('Give the business a name');
    setSaving(true);
    setError('');
    const body = {
      businessName: f.businessName,
      contactName: f.contactName,
      phone: f.phone,
      email: f.email,
      address: { line1: f.line1, line2: f.line2, city: f.city, postcode: f.postcode },
      potentialWeekly: f.potentialWeekly,
      interestedIn: f.interestedIn,
      termsDays: f.termsDays,
    };
    try {
      let saved;
      if (adding) {
        const { data } = await api.post('/trade/accounts', {
          ...body,
          stage: f.stage,
          nextStep: f.nextStep,
          nextStepDate: f.nextStep.trim() ? f.nextStepDate : '',
          ...(lead ? { lead: lead._id } : {}),
        });
        saved = data;
        if (first.text.trim()) {
          try {
            saved = (await api.post(`/trade/accounts/${saved._id}/activity`, { type: first.type, text: first.text })).data;
          } catch {
            // The business is saved either way; the note can be logged again from its page.
          }
        }
      } else {
        saved = (await api.patch(`/trade/accounts/${account._id}`, body)).data;
      }
      onSaved(saved);
    } catch (e) {
      setError(errorText(e));
      setSaving(false);
    }
  };

  return (
    <ModalShell onClose={onClose} width={620} ariaLabel={adding ? 'Add a business' : 'Edit business'}>
      <h2 style={{ margin: '0 0 14px', fontSize: 18 }}>{adding ? 'Add a business' : `Edit ${account.businessName}`}</h2>
      <div className="trade-form">
        {adding && (lead ? (
          <div className="ui-banner ui-banner--info trade-row">
            From Outreach: {lead.businessName}
            <Button size="sm" variant="muted" style={{ marginLeft: 'auto' }} onClick={() => setLead(null)}>Unlink</Button>
          </div>
        ) : <LeadPicker onPick={pickLead} />)}

        <div className="inv-grid2">
          <div><Label>Business</Label><Input autoFocus value={f.businessName} onChange={set('businessName')} placeholder="The Anchor" /></div>
          <div><Label>Contact</Label><Input value={f.contactName} onChange={set('contactName')} placeholder="Who you deal with" /></div>
          <div><Label>Phone</Label><Input type="tel" value={f.phone} onChange={set('phone')} /></div>
          <div><Label>Email</Label><Input type="email" value={f.email} onChange={set('email')} placeholder="Where invoices go" /></div>
        </div>

        <h4>Address</h4>
        <div className="inv-grid2">
          <div><Label>Line 1</Label><Input value={f.line1} onChange={set('line1')} /></div>
          <div><Label>Line 2</Label><Input value={f.line2} onChange={set('line2')} /></div>
          <div><Label>Town</Label><Input value={f.city} onChange={set('city')} placeholder="Hockley" /></div>
          <div><Label>Postcode</Label><Input value={f.postcode} onChange={e => setF(p => ({ ...p, postcode: e.target.value.toUpperCase() }))} /></div>
        </div>

        <h4>The sale</h4>
        <div className={adding ? 'inv-grid3' : 'inv-grid2'}>
          {adding && (
            <div>
              <Label>Stage</Label>
              <Select value={f.stage} onChange={set('stage')}>
                {STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </Select>
            </div>
          )}
          <div><Label hint="£ a week">Potential</Label><Input type="number" min="0" step="1" value={f.potentialWeekly} onChange={set('potentialWeekly')} placeholder="e.g. 40" /></div>
          <div><Label hint="days">Payment terms</Label><Input type="number" min="0" step="1" value={f.termsDays} onChange={set('termsDays')} placeholder="Default" /></div>
        </div>
        <div><Label>Interested in</Label><Input value={f.interestedIn} onChange={set('interestedIn')} placeholder="e.g. Basil for pizzas, amaranth to garnish" /></div>

        {adding && (
          <>
            <h4>So far</h4>
            <div className="trade-seg">
              {ACTIVITY_TYPES.filter(t => t.key !== 'note').map(t => (
                <button key={t.key} type="button" className={first.type === t.key ? 'is-on' : ''} onClick={() => setFirst(p => ({ ...p, type: t.key }))}>{t.label}</button>
              ))}
            </div>
            <Textarea rows={2} placeholder="What happened? (optional)" value={first.text} onChange={e => setFirst(p => ({ ...p, text: e.target.value }))} />
            <div className="trade-row" style={{ alignItems: 'flex-start' }}>
              <div style={{ flex: '1 1 260px' }}><Label hint="optional">Next step</Label><Input value={f.nextStep} onChange={set('nextStep')} placeholder="e.g. Drop samples Thursday" /></div>
              <div><Label>When</Label><Input type="date" style={{ width: 150 }} value={f.nextStepDate} onChange={set('nextStepDate')} /></div>
            </div>
            {f.nextStep.trim() && !f.nextStepDate && (
              <div className="trade-quick">
                {[['Tomorrow', 1], ['In 3 days', 3], ['Next week', 7]].map(([label, n]) => (
                  <button key={label} type="button" onClick={() => setF(p => ({ ...p, nextStepDate: addDays(today, n) }))}>{label}</button>
                ))}
              </div>
            )}
          </>
        )}

        {error && <div className="ui-banner ui-banner--bad">{error}</div>}
      </div>
      <div className="trade-form__actions">
        <Button variant="muted" onClick={onClose}>Cancel</Button>
        <Button variant="primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : adding ? 'Add business' : 'Save'}</Button>
      </div>
    </ModalShell>
  );
}
